import PropTypes from 'prop-types';
import React from 'react';
import { getCardById, getDisplayNumberById, getPlaceDisplayName } from '../../assets/cards/cards';
import { ReactComponent as BadBadge } from '../../assets/icons/badge_bad.svg';
import { ReactComponent as GoodBadge } from '../../assets/icons/badge_good.svg';
import { ReactComponent as NeutralBadge } from '../../assets/icons/badge_neutral.svg';
import { ReactComponent as NoCard } from '../../assets/icons/no_card.svg';
import { addToUserActions, hasUserAction } from '../../stored/UserActions';
import './Card.sass';

const truncate = (str, n) => {
	return (str.length > n) ? `${str.substr(0, n - 1)}…` : str;
};

const Card = ({ cardId, centered, foundAt, score, onClick, key, showOpenCardHint }) => {
	if (!cardId)
		return (
			<div className="card">
				<NoCard
					key={key}
					className="w-full h-full"
					preserveAspectRatio={centered ? 'xMidYMid meet' : 'xMinYMid meet'}
				/>
			</div>
		);

	const card = getCardById(cardId);
	const location = truncate(getPlaceDisplayName(foundAt), 14);

	const getFriendlynessIcon = () => {
		switch (card.info.friendlyness.badgeValue) {
			case 'good':
				return <GoodBadge className="card__badge absolute" />;
			case 'bad':
				return <BadBadge className="card__badge absolute" />;
			default:
				return <NeutralBadge className="card__badge absolute" />;
		}
	};

	const hasOpenedCard = hasUserAction(`opened_card_${cardId}`)

	return (
		<div
			className={`card relative ${(hasOpenedCard && onClick && showOpenCardHint) ? '' : 'card--animated'}`}
			onClick={() => {
				if (!hasOpenedCard) {
					addToUserActions(`opened_card_${cardId}`, {});
				}
				onClick();
				// @ts-ignore
				window.playSound(`button_${Math.floor(Math.random() * 4) + 1}`, 0.6);
				// @ts-ignore
				window.playSound('open_card');
			}}
			onKeyDown={() => { }}
			key={key}>
			<section className={(onClick && showOpenCardHint) ? 'card__animation' : null}>
				<svg
					className="w-full h-full filter drop-shadow-md"
					preserveAspectRatio={centered ? 'xMidYMid meet' : 'xMinYMid meet'}
					xmlns="http://www.w3.org/2000/svg"
					xmlnsXlink="http://www.w3.org/1999/xlink"
					viewBox="0 0 238 368"
					fill="none">
					<g filter={undefined /* `url(#filter0_d${card.id})` */}>
						<rect x="4" width="230" height="360" rx="36" fill={card.colors.primary} />
					</g>
					<rect x="4" width="230" height="360" rx="36" fill={card.colors.primary} />
					<rect x="7" y="3" width="224" height="354" rx="33" stroke={score === 6 ? `url(#pattern_p_${card.id})` : card.colors.primary} strokeWidth={score === 6 ? 6 : 0} />
					<rect x="4" width="230" height="360" rx="36" fill="black" fillOpacity="0.1" />
					<path
						d="M14 37C14 21.536 26.536 9 42 9H196C211.464 9 224 21.536 224 37V300C224 310.493 215.493 319 205 319H33C22.5066 319 14 310.493 14 300V37Z"
						fill={card.colors.primary}
					/>
					<rect
						x="19"
						y="15"
						width="200"
						height="187"
						rx="20"
						fill="url(#paint0_linear)"
						fillOpacity="0.7"
					/>
					<rect x="29" y="63" width="179" height="128" fill="url(#pattern0)" />
					<g filter="url(#filter1_dd)">
						<text
							fill="white"
							xmlSpace="preserve"
							fontFamily="Poppins"
							fontSize="18"
							fontWeight="bold"
							letterSpacing="0em">
							<tspan x="29" y="41.8">
								{card.shortDisplayName}
							</tspan>
						</text>
					</g>
					<text
						transform="translate(0 323)"
						fill="white"
						xmlSpace="preserve"
						fontFamily="Poppins"
						fontSize="18"
						fontStyle="italic"
						fontWeight="bold"
						letterSpacing="0em"
						textAnchor="middle">
						<tspan x="50%" y="19.8">
							{card.nickName}
						</tspan>
					</text>
					<g filter="url(#filter2_d)">
						<rect x="164" y="22" width="47" height="26" rx="13" fill='white' />
						<rect x="164" y="22" width="47" height="26" rx="13" opacity={score === 6 ? 0.5 : 0} fill={score === 6 ? `url(#paint1_linear_${card.id})` : 'white'} />
					</g>
					<rect
						x="19"
						y="208"
						width="200"
						height="31"
						rx="15.5"
						fill="black"
						fillOpacity="0.2"
					/>
					<text
						fill={score === 6 ? card.colors.primary : 'black'}
						fillOpacity="0.8"
						xmlSpace="preserve"
						fontFamily="Poppins"
						fontSize="18"
						fontWeight="bold"
						letterSpacing="0em"
						textAnchor="middle">
						<tspan x="187" y="41.8">
							{getDisplayNumberById(card.id)}
						</tspan>
					</text>

					<g filter="url(#filter5_d)">
						<text
							fill="white"
							xmlSpace="preserve"
							fontFamily="Poppins"
							fontSize="18"
							fontWeight="bold"
							letterSpacing="0em">
							<tspan x="57" y="229.8">
								{card.info.size.value}
							</tspan>
						</text>
					</g>
					<g filter="url(#filter4_d)">
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M47.5 223.443C47.5 216.847 42.1529 211.5 35.5569 211.5C28.9609 211.5 23.6137 216.847 23.6137 223.443C23.6137 225.928 24.3735 228.237 25.6733 230.149L21.5546 233.467C20.2442 234.522 20.139 236.481 21.3288 237.671C22.5186 238.861 24.4776 238.756 25.5332 237.446L28.8513 233.327C30.763 234.626 33.0719 235.385 35.5569 235.385C42.1529 235.385 47.5 230.039 47.5 223.443ZM35.5496 215.38C40.0069 215.38 43.6203 218.993 43.6203 223.45C43.6203 227.907 40.0069 231.52 35.5496 231.52C31.0923 231.52 27.479 227.907 27.479 223.45C27.479 218.993 31.0923 215.38 35.5496 215.38Z"
							fill="white"
						/>
					</g>
					<rect
						x="19"
						y="245"
						width="200"
						height="31"
						rx="15.5"
						fill="black"
						fillOpacity="0.2"
					/>
					<rect
						x="19"
						y="282"
						width="200"
						height="31"
						rx="15.5"
						fill="black"
						fillOpacity="0.2"
					/>
					<g filter="url(#filter6_d)">
						<text
							fill="white"
							xmlSpace="preserve"
							fontFamily="Poppins"
							fontSize="18"
							fontWeight="bold"
							letterSpacing="0em">
							<tspan x="57" y="266.8">
								{card.info.age.value}
							</tspan>
						</text>
					</g>
					<g filter="url(#filter7_d)">
						<text
							fill="white"
							xmlSpace="preserve"
							fontFamily="Poppins"
							fontSize="18"
							fontWeight="bold"
							letterSpacing="0em">
							<tspan x="58" y="302.8">
								{location}
							</tspan>
						</text>
					</g>
					<g filter="url(#filter8_d)">
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M30.4094 250.515L28.7979 250.388C27.6836 250.299 26.8508 249.323 26.9389 248.209C27.0271 247.095 28.003 246.262 29.1173 246.35L46.6127 247.734C47.727 247.822 48.5598 248.798 48.4717 249.913C48.3835 251.027 47.4076 251.86 46.2933 251.772L44.6818 251.644C44.8551 252.071 44.9356 252.566 44.8944 253.086L44.88 253.269C44.6799 255.799 44.6799 255.799 41.7088 258.328C41.5467 258.466 41.3758 258.611 41.1955 258.765L38.5314 260.637L40.8521 263.092C42.6743 265.227 43.3903 265.929 43.6231 266.766C43.7704 267.295 43.7242 267.878 43.6425 268.912L43.628 269.094C43.5869 269.615 43.4294 270.091 43.1909 270.485L44.8027 270.613C45.9171 270.701 46.7499 271.677 46.6617 272.791C46.5736 273.905 45.5977 274.738 44.4833 274.65L26.988 273.266C25.8737 273.178 25.0409 272.202 25.129 271.088C25.2172 269.973 26.1931 269.14 27.3074 269.229L28.9192 269.356C28.7457 268.929 28.6651 268.434 28.7062 267.914L28.7207 267.732C28.8025 266.698 28.8486 266.114 29.0773 265.615C29.4387 264.825 30.256 264.244 32.3904 262.423L35.0693 260.363L32.7336 258.097C30.9073 255.957 30.1978 255.185 29.971 254.291C29.8284 253.729 29.8767 253.118 29.9582 252.088L29.9726 251.906C30.0138 251.385 30.1712 250.909 30.4094 250.515Z"
							fill="white"
						/>
					</g>
					<g filter="url(#filter9_d)">
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M26.2781 292.054C26.5733 288.158 29.9705 285.239 33.8661 285.534L33.969 285.542C35.2338 285.638 36.1814 286.741 36.0856 288.006C35.9898 289.271 34.8868 290.218 33.622 290.122L33.5192 290.115C32.1532 290.011 30.9619 291.035 30.8584 292.401L30.8506 292.503C30.7548 293.768 29.6518 294.716 28.387 294.62C27.1222 294.524 26.1745 293.421 26.2704 292.156L26.2781 292.054Z"
							fill="white"
						/>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M43.6903 286.279C47.5859 286.574 50.5047 289.971 50.2095 293.867L50.2018 293.969C50.1059 295.234 49.0029 296.182 47.7381 296.086C46.4733 295.99 45.5256 294.887 45.6215 293.622L45.6293 293.52C45.7327 292.154 44.7093 290.962 43.3433 290.859L43.2404 290.851C41.9756 290.755 41.028 289.652 41.1238 288.387C41.2196 287.123 42.3226 286.175 43.5874 286.271L43.6903 286.279Z"
							fill="white"
						/>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M49.4653 303.691C49.1701 307.586 45.7729 310.505 41.8773 310.21L41.7744 310.202C40.5096 310.106 39.562 309.003 39.6578 307.739C39.7536 306.474 40.8566 305.526 42.1214 305.622L42.2243 305.63C43.5902 305.733 44.7815 304.71 44.885 303.344L44.8928 303.241C44.9886 301.976 46.0916 301.028 47.3564 301.124C48.6212 301.22 49.5689 302.323 49.4731 303.588L49.4653 303.691Z"
							fill="white"
						/>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M32.0532 309.466C28.1575 309.171 25.2388 305.773 25.5339 301.878L25.5417 301.775C25.6375 300.51 26.7405 299.562 28.0053 299.658C29.2701 299.754 30.2178 300.857 30.1219 302.122L30.1142 302.225C30.0107 303.591 31.0341 304.782 32.4001 304.885L32.503 304.893C33.7678 304.989 34.7154 306.092 34.6196 307.357C34.5238 308.622 33.4208 309.569 32.156 309.474L32.0532 309.466Z"
							fill="white"
						/>
					</g>
					<defs>
						<filter
							id={`filter0_d${card.id}`}
							x="0"
							y="0"
							width="238"
							height="368"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="4" />
							<feGaussianBlur stdDeviation="2" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.270588 0 0 0 0 0.686275 0 0 0 0 0.654902 0 0 0 0.5 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow"
								result="shape"
							/>
						</filter>
						<pattern
							id="pattern0"
							patternContentUnits="objectBoundingBox"
							width="1"
							height="1">
							<use
								xlinkHref="#image0"
								transform="translate(0 -0.00214897) scale(0.000903342 0.00126327)"
							/>
						</pattern>
						<pattern
							id={`pattern_p_${card.id}`}
							x="0"
							y="0"
							width="400%"
							height="100%"
							patternUnits="userSpaceOnUse">
							<rect x="-150%" y="0" width="200%" height="100%" fill={`url(#paint1_linear_${card.id}`} transform="rotate(-65)">
								<animate attributeName="x" from="-150%" to="50%" dur="4s" repeatCount="indefinite" />
							</rect>
							<rect x="-350%" y="0" width="200%" height="100%" fill={`url(#paint1_linear_${card.id}`} transform="rotate(-65)">
								<animate attributeName="x" from="-350%" to="-150%" dur="4s" repeatCount="indefinite" />
							</rect>
						</pattern>
						<filter
							id="filter1_dd"
							x="6.11597"
							y="3.58203"
							width="187"
							height="67.206"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="12" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="4" />
							<feGaussianBlur stdDeviation="2" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow"
								result="effect2_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow"
								result="shape"
							/>
						</filter>
						<filter
							id="filter2_d"
							x="162"
							y="22"
							width="51"
							height="30"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="2" />
							<feGaussianBlur stdDeviation="1" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow"
								result="shape"
							/>
						</filter>
						<filter
							id="filter3_d"
							x="178"
							y="168"
							width="50"
							height="50"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="2" />
							<feGaussianBlur stdDeviation="1" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow"
								result="shape"
							/>
						</filter>
						<filter
							id="filter4_d"
							x="18.5"
							y="211.5"
							width="31"
							height="31"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="2" />
							<feGaussianBlur stdDeviation="1" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow"
								result="shape"
							/>
						</filter>
						<filter
							id="filter5_d"
							x="55.8101"
							y="216.59"
							width="150"
							height="19.732"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="2" />
							<feGaussianBlur stdDeviation="1" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow"
								result="shape"
							/>
						</filter>
						<filter
							id="filter6_d"
							x="55.8821"
							y="253.68"
							width="109.146"
							height="17.464"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="2" />
							<feGaussianBlur stdDeviation="1" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow"
								result="shape"
							/>
						</filter>
						<filter
							id="filter7_d"
							x="57.116"
							y="289.68"
							width="150.68"
							height="22.108"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="2" />
							<feGaussianBlur stdDeviation="1" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow"
								result="shape"
							/>
						</filter>
						<filter
							id="filter8_d"
							x="22.9692"
							y="246.19"
							width="27.662"
							height="32.6194"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="2" />
							<feGaussianBlur stdDeviation="1" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow"
								result="shape"
							/>
						</filter>
						<filter
							id="filter9_d"
							x="23.5134"
							y="285.514"
							width="28.7167"
							height="28.7167"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							/>
							<feOffset dy="2" />
							<feGaussianBlur stdDeviation="1" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect1_dropShadow"
								result="shape"
							/>
						</filter>
						<linearGradient
							id="paint0_linear"
							x1="119"
							y1="15"
							x2="119"
							y2="202"
							gradientUnits="userSpaceOnUse">
							<stop stopColor="white" stopOpacity="0" />
							<stop offset="1" stopColor="white" />
						</linearGradient>
						<linearGradient
							id={`paint1_linear_${card.id}`}
							x1="100%"
							y1="0%"
							x2="0%"
							y2="0%">
							<stop offset="6.25%" stopColor={card.colors.primary} />
							<stop offset="43.75%" stopColor={card.colors.secondary} />
							<stop offset="56.25%" stopColor="white" stopOpacity="0.6" />
							<stop offset="68.75%" stopColor={card.colors.secondary} />
							<stop offset="100%" stopColor={card.colors.primary} />

							{/* old animation */}
							{/* <stop offset="0%" stopColor={card.colors.secondary}>
								<animate 
									attributeName="stop-color"
									values={`${card.colors.secondary}; ${card.colors.primary}; ${card.colors.secondary}`}
									dur="2.2s" 
									repeatCount="indefinite"
								/>
							</stop>
							<stop offset="100%" stopColor={card.colors.primary}>
								<animate 
									attributeName="stop-color"
									values={`${card.colors.primary}; ${card.colors.secondary}; ${card.colors.primary}`} 
									dur="2.2s" 
									repeatCount="indefinite"
								/>
							</stop> */}
						</linearGradient>
					</defs>

					<image
						x="19"
						y="30"
						width="200"
						height="187"
						preserveAspectRatio="xMidYMid meet"
						href={`/klokhuisscanner/pictures/${card.id}.png`}
					/>
					<g transform="translate(180,160) rotate(10)">
						{getFriendlynessIcon()}
					</g>
				</svg>
			</section>
		</div>
	);
};

export default Card;

Card.propTypes = {
	cardId: PropTypes.string,
	centered: PropTypes.bool,
	foundAt: PropTypes.string,
	score: PropTypes.number,
	onClick: PropTypes.func,
	key: PropTypes.any,
	showOpenCardHint: PropTypes.bool,
};

Card.defaultProps = {
	cardId: undefined,
	foundAt: undefined,
	score: undefined,
	centered: false,
	onClick: null,
	key: null,
	showOpenCardHint: false
};
